import { ActionIcon, Box, Button, Group, Menu, Stack, Text } from '@mantine/core';
import { DataTableColumn } from 'mantine-datatable';
import React, { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import CompanyWrapper from 'src/company/templates/components/CompanyWrapper';
import { User } from 'src/admin/users/types';
import { useDisclosure } from '@mantine/hooks';
import { IconDotsVertical, IconDownload, IconTrash } from '@tabler/icons-react';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { useDeleteUser } from 'src/admin/users/hooks/useDeleteUser';
import { useQueryClient } from '@tanstack/react-query';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import SecretMenuAddUserModal from './SecretMenuAddUserModal';
import { useUserEmailsExport } from './hooks/useUserEmailsExport';

export default function CompanyUsersPage() {
  const { downloadExport, isLoading } = useUserEmailsExport();

  return (
    <Stack gap="xs">
      <Group justify="flex-end">
        <Button
          leftSection={<IconDownload size={16} />}
          variant="light"
          loading={isLoading}
          onClick={downloadExport}
        >
          Export all user emails (excl. Afternoon)
        </Button>
      </Group>
      <CompanyWrapper contentComponent={CompanyUsersPageContent} />
    </Stack>
  );
}

function CompanyUsersPageContent({ company }: { company: Company }) {
  const queryClient = useQueryClient();
  const [opened, { open, close }] = useDisclosure(false);
  const {
    deleteUser,
    error: deleteUserError,
    reset: resetDeleteUser,
    isLoading: isDeletingUser,
  } = useDeleteUser({
    companyId: company?.id,
  });
  const [userToDelete, setUserToDelete] = useState<null | User>(null);
  const columns: DataTableColumn<User>[] = [
    {
      accessor: 'lastName',
      title: 'Last name',
      sortable: true,
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.lastName}
        </Text>
      ),
    },
    {
      accessor: 'firstName',
      title: 'First name',
      sortable: true,
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.firstName}
        </Text>
      ),
    },
    {
      accessor: 'emailAddress',
      title: 'Email address',
      sortable: true,
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.emailAddress}
        </Text>
      ),
    },
    {
      accessor: 'userRole',
      title: 'User role',
      sortable: true,
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.userRole}
        </Text>
      ),
    },
    {
      accessor: 'userStatus',
      title: 'User status',
      sortable: true,
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.userStatus}
        </Text>
      ),
    },
    {
      accessor: 'id',
      title: 'User ID',
      sortable: true,
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.id}
        </Text>
      ),
    },
    {
      accessor: 'actions',
      title: '',
      render: record => (
        <Box ta="right">
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => {
                  resetDeleteUser();
                  setUserToDelete(record);
                }}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Box>
      ),
    },
  ];

  const handleDeleteConfirm = async () => {
    try {
      if (!userToDelete) return;
      await deleteUser(userToDelete.id);
      queryClient.invalidateQueries({
        queryKey: ['admin', 'companies', company.id, 'users'],
      });
      setUserToDelete(null);
    } catch (e) {
      setUserToDelete(null);
      notifyAndLogError(
        'Failed to delete user',
        'An error occurred while deleting the user. Please try again.',
        e,
        { userId: userToDelete?.id },
      );
    }
  };

  return (
    <>
      <SecretMenuAddUserModal opened={opened} close={close} />
      <ConfirmDeleteModal
        opened={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete user"
        message={`Are you sure you want to delete ${userToDelete?.firstName} ${userToDelete?.lastName}? This action cannot be undone.`}
        error={deleteUserError}
        isLoading={isDeletingUser}
      />
      <SimpleTable<User>
        resource={`admin/companies/${company.id}/users`}
        columns={columns}
        title="Users"
        primaryButton={{
          label: 'Add user',
          onClick: open,
          variant: 'primary',
        }}
        showCount
        enableSearch
        pageSize={10}
        mode="client"
      />
    </>
  );
}
